import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "../../../utils/cmsAxios";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const normalizeInquiry = (raw) => {
  const id = raw.id ?? raw.help_id ?? raw.inquiry_id ?? raw.ticket_id ?? raw.uuid;
  const category = raw.category ?? raw.category_name ?? raw.type ?? "미분류";
  const name = raw.name ?? raw.user_name ?? "";
  const email = raw.email ?? "";
  const phone = raw.phone ?? "";
  const contentRaw = String(raw.content ?? raw.message ?? raw.body ?? "");
  let title = raw.title ?? raw.subject ?? "";
  let content = contentRaw;
  let content_preview = contentRaw;

  if (!title) {
    const trimmed = contentRaw.replace(/\r\n/g, "\n").trimStart();
    const [firstLine, ...rest] = trimmed.split("\n");
    title = (firstLine || "").slice(0, 30) || "(제목 없음)";
    content_preview = rest.join("\n").trimStart() || "";
  }
  const created_at = raw.created_at ?? raw.createdAt ?? raw.inserted_at ?? raw.created ?? null;
  const answer = raw.answer ?? raw.reply ?? "";
  const answered_at = raw.answered_at ?? raw.reply_at ?? null;
  const status = raw.status ?? (answer && String(answer).trim() ? "answered" : "pending");

  const fromArray = Array.isArray(raw.attachments) ? raw.attachments : [];
  const fromKeys = ["attachment1", "attachment2", "attachment3"]
    .map((k) => raw[k])
    .filter(Boolean)
    .map((url) => ({ url, filename: String(url).split("/").pop() }));
  const attachments = [...fromArray, ...fromKeys].map((a, i) => ({
    url: a.url ?? a,
    filename: a.filename ?? (String(a.url ?? a).split("/").pop() || `file_${i + 1}`),
  }));

  return {
    id, category, name, email, phone,
    title, content, content_preview,
    created_at, status, answer, answered_at,
    attachments,
    _raw: raw,
  };
};

const formatDate = (d) => {
  if (!d) return "-";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "-";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
};

export default function CmsInquiryDetail({ type = "admin" }) {
  const isAdmin = type === "admin";
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewImage, setPreviewImage] = useState(null); // 확대해서 볼 이미지 URL

  const backToList = () => {
    // 목록 복귀 시 상태/스크롤 복원
    const state = location.state || {};
    navigate(state.from || "/cms/inquiry", { state });
    if (state.scrollY != null) {
      requestAnimationFrame(() => window.scrollTo(0, state.scrollY));
    }
  };

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API}/help/${id}`, {
        headers: { Accept: "application/json" },
        timeout: 15000,
        validateStatus: () => true,
      });
      const ct = res.headers?.["content-type"] || "";
      const looksHtml = typeof res.data === "string" || ct.includes("text/html");
      if (res.status >= 200 && res.status < 300 && !looksHtml) {
        const norm = normalizeInquiry(res.data);
        setItem(norm);
        setAnswerText(norm.answer || "");
      } else {
        setErrorMsg("상세를 불러오지 못했습니다.");
      }
    } catch (e) {
      setErrorMsg("네트워크 오류로 상세를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const submitAnswer = async () => {
    if (!isAdmin || !item) return;
    try {
      setSubmitting(true);
      const url = `${API}/help/${item.id}/status`;
      const res = await axios.patch(
        url,
        { status: "answered", answer: answerText },
        { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
      );
      if (res.status < 200 || res.status >= 300) {
        alert("답변 저장 실패(서버 응답 오류).");
        return;
      }
      await fetchDetail();
      alert("답변이 저장되었습니다.");
    } catch (e) {
      alert("답변 저장에 실패했습니다. 콘솔을 확인하세요.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_40px_80px_-24px_rgba(0,0,0,0.12)] overflow-hidden">
        <div className="flex">

          <div className="flex-1 min-w-0 bg-neutral-50/70">
            <main className="pt-12 flex flex-col gap-4 p-4 overflow-x-hidden w-full">
              <section className="p-4">
                <div className="flex items-center justify-between pb-4">
                  <h2 className="text-xl font-semibold">1:1 문의 &gt; 상세</h2>
                </div>

                {errorMsg && (
                  <div className="mb-3 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{errorMsg}</div>
                )}

                {loading || !item ? (
                  <div className="p-6 text-gray-500">불러오는 중…</div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-lg text-black font-extrabold">회원 기본 정보</div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full table-fixed text-sm border-collapse">
                        <colgroup>
                          <col className="w-28 md:w-32" />
                          <col />
                          <col className="w-28 md:w-32" />
                          <col />
                        </colgroup>
                        <tbody className="divide-y divide-gray-200">
                          <tr>
                            <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">이름</th>
                            <td className="px-4 py-2 border-l bg-white border-gray-200">{item.name || "-"}</td>
                            <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2 border-l border-gray-200">이메일</th>
                            <td className="px-4 py-2 border-l bg-white border-gray-200 break-all">{item.email || "-"}</td>
                          </tr>
                          <tr className="border-gray-200">
                            <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">휴대폰 번호</th>
                            <td className="px-4 py-2 bg-white border-l border-gray-200">{item.phone || "-"}</td>
                            <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2 border-l border-gray-200">등록일시</th>
                            <td className="px-4 py-2 bg-white border-l border-gray-200">{formatDate(item.created_at)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="text-lg text-black font-extrabold mb-1">문의 내용</div>
                    <table className="ring-2 rounded-md ring-gray-200 border-gray-200 border-collapse bg-white">
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200">문의 종류</th>
                          <td className="px-4 py-2 w-full bg-white text-gray-800 border-r border-gray-200">{item.category || "-"}</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200 align-center">내용</th>
                          <td className="p-4" colSpan={3}>
                            <div className="rounded-lg bg-white min-h-[12rem] whitespace-pre-line break-words">
                              {item.content || "-"}
                            </div>
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-3 whitespace-nowrap border-r border-gray-200 align-top">첨부 파일</th>
                          <td className="px-4 py-3" colSpan={3}>
                            {item.attachments?.length ? (
                              <div className="flex flex-wrap gap-3">
                                {item.attachments.map((a, i) => {
                                  const rawUrl = a.url || "";

                                  // 1) 절대 URL인지 확인 (예: 이미 https:// 로 저장된 경우)
                                  const isAbsolute = /^https?:\/\//.test(rawUrl);

                                  // 2) 기존에 /app/uploads 로 저장돼 있던 케이스 처리
                                  let pathPart = rawUrl;
                                  if (rawUrl.startsWith("/app/uploads/")) {
                                    pathPart = rawUrl.replace("/app/uploads/", "");
                                  }

                                  // 3) 새 구조: DB에 "help/파일명" 이런 식으로 저장된 경우
                                  //    → 앞의 슬래시는 제거
                                  pathPart = pathPart.replace(/^\/+/, "");

                                  // 4) 최종 표시용 URL 만들기
                                  const UPLOAD_BASE =
                                    process.env.REACT_APP_UPLOAD_PUBLIC_BASE_URL ||
                                    "https://wizmarket.ai/uploads";

                                  const fullUrl = isAbsolute
                                    ? rawUrl
                                    : `${UPLOAD_BASE}/${pathPart}`;

                                  return (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => setPreviewImage(fullUrl)}
                                      className="flex flex-col items-start gap-1"
                                    >
                                      {/* 썸네일 이미지 */}
                                      <img
                                        src={fullUrl}
                                        alt={a.filename}
                                        className="w-24 h-24 object-cover rounded-md border border-gray-200 bg-gray-50"
                                      />
                                      {/* 파일명 텍스트 */}
                                      <span className="text-xs text-gray-600 break-all max-w-[100px] text-left">
                                        {a.filename}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-gray-400">첨부 없음</span>
                            )}

                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {isAdmin ? (
                      <div className="border-t">
                        <div className="text-xl text-black font-extrabold py-4">1:1 문의 답변</div>

                        {/* <div className="grid grid-cols-12 gap-4 pt-3">
                          <div className="col-span-12 md:col-span-4">
                            <label className="block text-m text-gray-500 pt-1">등록(수정)자</label>
                            <div className="w-full h-9 px-3 flex items-center rounded-md border border-gray-200 bg-white text-gray-500">Admin</div>
                          </div>
                          <div className="col-span-12 md:col-span-4">
                            <label className="block text-m text-gray-500 pt-1">등록(수정)일시</label>
                            <div className="w-full h-9 px-3 flex items-center rounded-md border border-gray-200 bg-white text-gray-500">
                              {item.answered_at ? formatDate(item.answered_at) : formatDate(new Date())}
                            </div>
                          </div>
                        </div>

                        <div className="text-lg text-black font-extrabold pt-6">답변 내용</div>
                        <textarea
                          className="w-full h-40 px-3 py-2 border rounded-lg"
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="고객에게 전달될 답변을 작성하세요."
                        /> */}

                        <div className="space-y-4">
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full table-fixed text-sm border-collapse">
                              <colgroup>
                                <col className="w-28 md:w-32" />
                                <col />
                                <col className="w-28 md:w-32" />
                                <col />
                              </colgroup>
                              <tbody className="divide-y divide-gray-200">
                                <tr>
                                  <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">답변등록자</th>
                                  <td className="px-4 py-2 border-l bg-white border-gray-200">Admin</td>
                                  <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2 border-l border-gray-200">답변 등록일시</th>
                                  <td className="px-4 py-2 border-l bg-white border-gray-200 break-all">
                                    {item.answered_at ? formatDate(item.answered_at) : "-"}
                                  </td>
                                </tr>
                                <tr className="border-gray-200">
                                  <th className="bg-gray-50 text-gray-500 font-medium text-left px-4 py-2">답변 내용</th>
                                  <td className="px-4 py-2 bg-white border-l border-gray-200" colSpan={3}>
                                    <textarea
                                      className="w-full h-40 px-3 py-2 border rounded-lg min-h-[12rem]"
                                      value={answerText}
                                      onChange={(e) => setAnswerText(e.target.value)}
                                      placeholder="고객에게 전달될 답변을 작성하세요."
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>


                        <div className="flex justify-center gap-2 pt-2">
                          {/* <button
                            className="px-4 h-10 rounded-lg border hover:bg-gray-50"
                            onClick={() => setAnswerText(item.answer || "")}
                            disabled={submitting}
                          >
                            원래대로
                          </button> */}
                          <button
                            className="px-4 h-10 rounded-lg bg-[#7864F91A]/10 text-[#7864F9] hover:bg-[#7864F9]/40 disabled:opacity-50"
                            onClick={submitAnswer}
                            disabled={submitting || !answerText.trim()}
                          >
                            {submitting ? "저장 중..." : "저장"}
                          </button>
                          <button onClick={backToList} className="px-3 py-1.5 rounded-md border hover:bg-gray-200">이전</button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t">
                        <div className="text-sm text-gray-500 mb-2">답변</div>
                        <div className="whitespace-pre-line min-h-[4rem]">
                          {item.answer?.trim() ? item.answer : "아직 답변이 준비되지 않았습니다."}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {previewImage && (
                <div
                  className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center"
                  onClick={() => setPreviewImage(null)} // 배경 클릭 시 닫기
                >
                  <div
                    className="max-w-[90vw] max-h-[90vh] bg-black/0"
                    onClick={(e) => e.stopPropagation()} // 이미지 클릭 시 배경 클릭 이벤트 막기
                  >
                    <img
                      src={previewImage}
                      alt="첨부 이미지 미리보기"
                      className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
                    />
                    <div className="mt-3 text-center">
                      <button
                        type="button"
                        onClick={() => setPreviewImage(null)}
                        className="inline-flex items-center px-4 py-1.5 rounded-md bg-white text-sm text-gray-800 shadow hover:bg-gray-100"
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              )}


            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
